import { useSyncExternalStore } from 'react'
import { entityImages, type ImageEntry } from '../data/guide/entityImages'
import { useWorld } from '../contexts/WorldDataContext'
import type { ClassId } from '../types/progression'
import type { WorldData } from '../types/world'

export type ResolvedImageEntry = ImageEntry & {
  resolvedMonsterIds: number[]
  ambiguous: boolean
  notInCatalog: boolean
}

const byClass = new Map(entityImages.filter((e) => e.classId).map((e) => [e.classId!, e]))
export const imageEntryForClass = (id: ClassId) => byClass.get(id)

const cache = new WeakMap<WorldData, { byMonster: Map<number, ImageEntry>; resolved: ResolvedImageEntry[] }>()

/** Une el registro con 06_monsters. Por nombre solo se asigna con una única coincidencia exacta. */
export function resolveImageEntries(w: WorldData) {
  const hit = cache.get(w)
  if (hit) return hit
  const nameIndex = new Map<string, number[]>()
  w.monsters.forEach((m) => {
    const k = m.name.trim().toLowerCase()
    nameIndex.set(k, [...(nameIndex.get(k) ?? []), m.id])
  })
  const byMonster = new Map<number, ImageEntry>()
  const resolved: ResolvedImageEntry[] = entityImages.map((e) => {
    if (e.kind === 'clase') return { ...e, resolvedMonsterIds: [], ambiguous: false, notInCatalog: false }
    if (e.monsterId !== undefined) {
      const exists = w.monsters.has(e.monsterId)
      if (exists) byMonster.set(e.monsterId, e)
      return { ...e, resolvedMonsterIds: exists ? [e.monsterId] : [], ambiguous: false, notInCatalog: !exists }
    }
    const ids = nameIndex.get((e.monsterName ?? '').trim().toLowerCase()) ?? []
    if (ids.length === 1) byMonster.set(ids[0], e)
    return { ...e, resolvedMonsterIds: ids, ambiguous: ids.length > 1, notInCatalog: ids.length === 0 }
  })
  const out = { byMonster, resolved }
  cache.set(w, out)
  return out
}

/** Entrada por ID de monstruo/NPC. Sin catálogo cargado, solo las entradas con ID fijo. */
export const imageEntryForMonster = (w: WorldData | undefined, monsterId: number): ImageEntry | undefined =>
  w ? resolveImageEntries(w).byMonster.get(monsterId) : entityImages.find((e) => e.monsterId === monsterId)

/** Misma asociación por ID en cualquier componente. */
export function useMonsterImage(monsterId: number | undefined): ImageEntry | undefined {
  const { data } = useWorld()
  return monsterId === undefined ? undefined : imageEntryForMonster(data, monsterId)
}

/* Registro de imágenes que no cargan, para el panel del staff. */
const failed = new Set<string>()
const listeners = new Set<() => void>()
let snapshot: string[] = []

export function markImageFailed(src: string) {
  if (failed.has(src)) return
  failed.add(src)
  snapshot = Array.from(failed)
  listeners.forEach((l) => l())
}

export function useFailedImages() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => snapshot,
  )
}
